import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import '../styles/formStyles.css';
import { showAppAlert } from '../utils/alertService';

function GenerateLinkModal({ isOpen, onClose, company, client }) {
  const printableContentRef = useRef();

  // Objeto de configuração para ativar/desativar botões de compartilhamento no desktop
  const shareOptionsConfig = {
    whatsapp: true,
    facebook: true,
    twitter: true, // X
    linkedin: true,
    email: true,
  };

  if (!isOpen || !company) {
    return null;
  }
  
  // Garante que a URL base esteja correta e seja acessível na rede local.
  // O WhatsApp não reconhece 'localhost' como um link válido em dispositivos móveis.
  let baseAppUrl = window.location.origin;
  if (baseAppUrl.includes('localhost')) {
    baseAppUrl = `http://${window.location.hostname}:${window.location.port}`;
  }

  // Codifica os dados da empresa como parâmetros de URL
  const queryParams = new URLSearchParams({
    name: company.nomeFantasia || '',
    logo: company.urlLogo || '',
    ...(client ? {
      clientName: client.nome || '',
      clientPhone: client.telefone || '',
    } : {}),
  }).toString();

  const registrationLink = `${baseAppUrl}/#/register-client/${company.id}?${queryParams}`;  

  const handlePrint = () => {
    const content = printableContentRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Imprimir Link de Cadastro</title>');
    printWindow.document.write(`
      <style>
        body { font-family: sans-serif; text-align: center; }
        a { word-break: break-all; }
      </style>
    `);
    printWindow.document.write('</head><body>');
    printWindow.document.write(content);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    
    // Timeout para garantir que o conteúdo seja carregado antes de imprimir em alguns navegadores
    setTimeout(() => {
        printWindow.print();
        printWindow.close();
    }, 500);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationLink);
      showAppAlert('success', 'Link copiado para a área de transferência!');
    } catch (err) {
      console.error('Falha ao copiar o link: ', err);
      showAppAlert('error', 'Falha ao copiar o link.');
    }
  };

  const handleShareWhatsApp = () => {
    const introMessage = `Olá! Cadastre-se no programa de fidelidade de ${company.nomeFantasia || 'nossa loja'} e comece a ganhar pontos agora mesmo!`;
    // Adicionar quebras de linha antes do link ajuda o WhatsApp a reconhecê-lo como clicável.
    const message = `${introMessage}\n\n${registrationLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(registrationLink)}`;
    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareTwitter = () => {
    const text = `Cadastre-se no programa de fidelidade de ${company.nomeFantasia || 'nossa loja'}!`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(registrationLink)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareLinkedIn = () => {
    const title = `Programa de Fidelidade: ${company.nomeFantasia || 'Nossa Loja'}`;
    const summary = `Cadastre-se no programa de fidelidade de ${company.nomeFantasia || 'nossa loja'} e comece a ganhar pontos agora mesmo!`;
    const linkedInUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(registrationLink)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareEmail = () => {
    const subject = `Convite para o Programa de Fidelidade: ${company.nomeFantasia || 'Nossa Loja'}`;
    const body = `Olá!\n\nCadastre-se no programa de fidelidade de ${company.nomeFantasia || 'nossa loja'} e comece a ganhar pontos agora mesmo!\n\nClique no link: ${registrationLink}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
  };

  const handleNativeShare = async () => {
    const introMessage = `Olá! Cadastre-se no programa de fidelidade de ${company.nomeFantasia || 'nossa loja'} e comece a ganhar pontos agora mesmo!`;

    try {
      await navigator.share({
        title: `Cadastro Fidelidade - ${company.nomeFantasia || 'Nossa Loja'}`,
        text: introMessage, // Alguns apps usam o 'text' como preview
        url: registrationLink,
      });
      } catch (err) {
        // Não mostra erro se o usuário simplesmente fechar a janela de compartilhamento
        if (err.name !== 'AbortError') {
          console.error('Erro ao usar a API de compartilhamento: ', err);
          showAppAlert('error', 'Não foi possível abrir a janela de compartilhamento.');
        }
      }
    };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
        <button onClick={onClose} className="modal-close-button">
          &times;
        </button>
        <div ref={printableContentRef}>
          <h2>{client ? 'Link de Cadastro Personalizado' : 'Link de Cadastro de Cliente'}</h2>
          <p>{client
            ? `O nome e o telefone de ${client.nome || 'este contato'} já estarão preenchidos no formulário.`
            : 'Aponte a câmera do celular para o QR Code ou utilize o link abaixo para que seus clientes possam se cadastrar no seu programa de fidelidade.'}
          </p>
          <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'center' }}>
            <QRCodeSVG value={registrationLink} size={256} level={"H"} />
          </div>
          <p><strong>Link para cadastro:</strong></p>
          <p style={{ wordBreak: 'break-all' }}>
            <a href={registrationLink} target="_blank" rel="noopener noreferrer">{registrationLink}</a>
          </p>
        </div>
        <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
          <button 
            onClick={handleCopyLink} 
            className="button-style" 
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <i className="bi bi-copy" style={{ marginRight: '8px' }}></i> Copiar Link
          </button>

          {/* Renderiza o botão de compartilhamento nativo se disponível (mobile) */}
          {navigator.share && (
            <button 
              onClick={handleNativeShare} 
              className="button-style" 
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <i className="bi bi-share-fill" style={{ marginRight: '8px' }}></i> Compartilhar
            </button>
          )}

          {/* Renderiza botões específicos como fallback para desktop */}
          {!navigator.share && (
            <>
              {shareOptionsConfig.whatsapp && (
                <button onClick={handleShareWhatsApp} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <i className="bi bi-whatsapp" style={{ marginRight: '8px' }}></i> WhatsApp
                </button>
              )}
              {shareOptionsConfig.facebook && (
                <button onClick={handleShareFacebook} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <i className="bi bi-facebook" style={{ marginRight: '8px' }}></i> Facebook
                </button>
              )}
              {shareOptionsConfig.twitter && (
                <button onClick={handleShareTwitter} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <i className="bi bi-twitter-x" style={{ marginRight: '8px' }}></i> X
                </button>
              )}
              {shareOptionsConfig.linkedin && (
                <button onClick={handleShareLinkedIn} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <i className="bi bi-linkedin" style={{ marginRight: '8px' }}></i> LinkedIn
                </button>
              )}
              {shareOptionsConfig.email && (
                <button onClick={handleShareEmail} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <i className="bi bi-envelope-fill" style={{ marginRight: '8px' }}></i> Email
                </button>
              )}
            </>
          )}

          <button 
            onClick={handlePrint} 
            className="button-style"
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <i className="bi bi-printer-fill" style={{ marginRight: '8px' }}></i> Imprimir QR Code
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenerateLinkModal;
